import { createApi } from '@reduxjs/toolkit/query/react'
import getCustomFetchBase from '../custom-fetch-base'
import Strapi, {
    EndpointQueryParameters,
    StrapiCreate,
    StrapiSingle,
    StrapiUpdate,
} from '../../types/strapi/strapi'
import Task from '../../types/api/task'
import getQuery from '../../helpers/api/get-query'
import { shipmentsApi } from './shipments-api'

export const tasksApi = createApi({
    reducerPath: 'tasksApi',
    baseQuery: getCustomFetchBase({ baseUrl: import.meta.env.VITE_API_URL }),
    tagTypes: ['Tasks'],
    endpoints: builder => ({
        getTasks: builder.query<Strapi<Task>, EndpointQueryParameters | void>({
            query: getQuery('/tasks/my'),
            providesTags: ['Tasks'],
        }),
        updateTask: builder.mutation<StrapiSingle<Task>, StrapiUpdate<Task>>({
            query: ({ docId, body }) => ({
                url: `/tasks/${docId}`,
                method: `PUT`,
                body,
            }),
            invalidatesTags: ['Tasks'],
        }),
        createTask: builder.mutation<StrapiSingle<Task>, StrapiCreate<Task>>({
            query: body => ({
                url: '/tasks',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Tasks'],
        }),
        approveAssignmentTask: builder.mutation<{ task: Task; shipment: unknown }, string>({
            query: docId => ({
                url: `/tasks/my/${docId}/approve-assignment`,
                method: 'POST',
            }),
            invalidatesTags: ['Tasks'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(shipmentsApi.util.invalidateTags(['Shipments']))
                } catch {
                    /* noop */
                }
            },
        }),
        rejectAssignmentTask: builder.mutation<{ task: Task; shipment: unknown }, string>({
            query: docId => ({
                url: `/tasks/my/${docId}/reject-assignment`,
                method: 'POST',
            }),
            invalidatesTags: ['Tasks'],
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled
                    dispatch(shipmentsApi.util.invalidateTags(['Shipments']))
                } catch {
                    /* noop */
                }
            },
        }),
        approveAssemblyTask: builder.mutation<{ task: Task; nextTask: Task }, string>({
            query: docId => ({
                url: `/tasks/my/${docId}/approve-assembly`,
                method: 'POST',
            }),
            invalidatesTags: ['Tasks'],
        }),
        rejectAssemblyTask: builder.mutation<
            { task: Task; nextTask: Task },
            { docId: string; comment: string; attachmentIds?: number[] }
        >({
            query: ({ docId, comment, attachmentIds }) => ({
                url: `/tasks/my/${docId}/reject-assembly`,
                method: 'POST',
                body: { comment, attachmentIds },
            }),
            invalidatesTags: ['Tasks'],
        }),
        completeAssemblyPrep: builder.mutation<{ task: Task; nextTask: Task }, string>({
            query: docId => ({
                url: `/tasks/my/${docId}/complete-assembly-prep`,
                method: 'POST',
            }),
            invalidatesTags: ['Tasks'],
        }),
        completeAssemblyRework: builder.mutation<{ task: Task; nextTask: Task }, string>({
            query: docId => ({
                url: `/tasks/my/${docId}/complete-assembly-rework`,
                method: 'POST',
            }),
            invalidatesTags: ['Tasks'],
        }),
    }),
})

export const {
    useGetTasksQuery,
    useUpdateTaskMutation,
    useCreateTaskMutation,
    useApproveAssignmentTaskMutation,
    useRejectAssignmentTaskMutation,
    useApproveAssemblyTaskMutation,
    useRejectAssemblyTaskMutation,
    useCompleteAssemblyPrepMutation,
    useCompleteAssemblyReworkMutation,
} = tasksApi
